// MySQL renderer-side schema introspection.
//
// S7 items 2 + 8 — every `information_schema` scan here is PAGED: a
// `LIMIT n OFFSET m` window keyed by the query's own `ORDER BY`,
// `INTROSPECTION_PAGE_ROWS` rows at a time, issued through the shared
// per-connection introspection lane (`introspectionSemaphore.ts`, max 2 in
// flight) and sunk page by page so `mysqlSchemaCache` can merge + republish
// incrementally.
//
// Paging keys, one per artefact:
//   relations  `(TABLE_NAME)`
//   columns    `(TABLE_NAME, ORDINAL_POSITION)`
//   indexes    `(TABLE_NAME, INDEX_NAME, SEQ_IN_INDEX)` — group `table|index`
//   fks        `(TABLE_NAME, CONSTRAINT_NAME, ORDINAL_POSITION)`
//                                                      — group `table|constraint`
//   routines   `(ROUTINE_NAME)`
//
// The O(1) probes (`@@sql_mode` / `@@lower_case_table_names` / `VERSION()`,
// the database list) stay single queries through the same lane.

import type {
  MysqlSchemaColumn,
  MysqlSchemaForeignKey,
  MysqlSchemaIndex,
  MysqlSchemaObject,
  MysqlSchemaObjectType,
  MysqlSchemaRoutine,
  MysqlSchemaSnapshot,
} from './mysqlSchemaTypes';
import { fetchMysqlRoutineParams } from './mysqlRoutineIntrospection';
import type { IntrospectionRow, PagedIntrospectionOutcome } from './introspectionPaging';
import {
  COLUMN_ROW_BUDGET,
  FK_ROW_BUDGET,
  INDEX_ROW_BUDGET,
  ROUTINE_ROW_BUDGET,
  TABLE_ROW_BUDGET,
} from './introspectionBudgets';
import {
  fetchIntrospectionProbeRows,
  fetchPagedArtefact,
  type PagedFetchOptions,
} from './introspectionQuery';

export type { PagedFetchOptions };

/** One page's worth of aggregated items, handed to phase 2's merge. */
export type MysqlPageSink<TItem> = (items: readonly TItem[]) => void | Promise<void>;

/** A relation-scoped page item — the relation it belongs to plus the payload. */
export type MysqlRelationScoped<TItem> = { readonly table: string; readonly item: TItem };

async function probeRows(
  connectionId: string,
  database: string,
  label: string,
  sql: string,
): Promise<readonly IntrospectionRow[]> {
  return fetchIntrospectionProbeRows({ engine: 'mysql', connectionId, database, label, sql });
}

async function pageMysqlArtefact<TItem>(args: {
  connectionId: string;
  database: string;
  label: string;
  artefact: Parameters<typeof fetchPagedArtefact>[0]['artefact'];
  totalBudget: number;
  sqlForPage: (pageClause: string) => string;
  aggregate: (rows: readonly IntrospectionRow[]) => TItem[];
  sink: MysqlPageSink<TItem>;
  groupKeyOf?: (row: IntrospectionRow) => string;
  shouldAbort?: () => boolean;
}): Promise<PagedIntrospectionOutcome> {
  return fetchPagedArtefact<TItem>({ engine: 'mysql', dialect: 'limit-offset', ...args });
}

function toNullableString(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  return String(value);
}

export async function fetchMysqlDatabases(
  connectionId: string,
  database: string,
): Promise<string[]> {
  const sql = `SELECT SCHEMA_NAME FROM information_schema.SCHEMATA
    WHERE SCHEMA_NAME NOT IN ('information_schema', 'performance_schema', 'mysql', 'sys')
    ORDER BY SCHEMA_NAME;`;
  const rows = await probeRows(connectionId, database, 'databases', sql);
  return rows.map((row) => String(row.SCHEMA_NAME ?? ''));
}

export type MysqlRelationEntry = { name: string; type: MysqlSchemaObjectType };

export function buildMysqlRelationsPageSql(pageClause: string): string {
  return `SELECT TABLE_NAME, TABLE_TYPE FROM information_schema.TABLES
    WHERE TABLE_SCHEMA = DATABASE()
    ORDER BY TABLE_NAME
    ${pageClause}`;
}

export function aggregateMysqlRelationRows(
  rows: readonly IntrospectionRow[],
): MysqlRelationEntry[] {
  const out: MysqlRelationEntry[] = [];
  for (const row of rows) {
    const name = String(row.TABLE_NAME ?? '').trim();
    if (!name) continue;
    const rawType = String(row.TABLE_TYPE ?? '').toUpperCase();
    out.push({ name, type: rawType === 'VIEW' ? 'view' : 'table' });
  }
  return out;
}

/** Page the relation list into `sink` — PHASE 1 of the tiered load. */
export async function fetchMysqlRelationPages(
  connectionId: string,
  database: string,
  sink: MysqlPageSink<MysqlRelationEntry>,
  options?: PagedFetchOptions,
): Promise<void> {
  await pageMysqlArtefact<MysqlRelationEntry>({
    connectionId,
    database,
    label: 'relations',
    artefact: 'tables',
    totalBudget: TABLE_ROW_BUDGET,
    sqlForPage: buildMysqlRelationsPageSql,
    aggregate: aggregateMysqlRelationRows,
    sink,
    shouldAbort: options?.shouldAbort,
  });
}

/** Accumulating wrapper over {@link fetchMysqlRelationPages}. */
async function fetchTablesAndViews(
  connectionId: string,
  database: string,
): Promise<MysqlRelationEntry[]> {
  const out: MysqlRelationEntry[] = [];
  await fetchMysqlRelationPages(connectionId, database, (page) => {
    out.push(...page);
  });
  return out;
}

export function buildMysqlColumnsPageSql(pageClause: string): string {
  return `SELECT TABLE_NAME, COLUMN_NAME, COLUMN_TYPE, IS_NULLABLE, COLUMN_DEFAULT,
    COLUMN_KEY, EXTRA, ORDINAL_POSITION
    FROM information_schema.COLUMNS
    WHERE TABLE_SCHEMA = DATABASE()
    ORDER BY TABLE_NAME, ORDINAL_POSITION
    ${pageClause}`;
}

export function aggregateMysqlColumnRows(
  rows: readonly IntrospectionRow[],
): MysqlRelationScoped<MysqlSchemaColumn>[] {
  const out: MysqlRelationScoped<MysqlSchemaColumn>[] = [];
  for (const row of rows) {
    const table = String(row.TABLE_NAME ?? '');
    if (!table) continue;
    out.push({
      table,
      item: {
        name: String(row.COLUMN_NAME ?? ''),
        dataType: String(row.COLUMN_TYPE ?? ''),
        isNullable: String(row.IS_NULLABLE ?? '') === 'YES',
        isPrimaryKey: String(row.COLUMN_KEY ?? '') === 'PRI',
        isAutoIncrement: String(row.EXTRA ?? '').includes('auto_increment'),
        defaultValue: toNullableString(row.COLUMN_DEFAULT),
        ordinalPosition: Number(row.ORDINAL_POSITION ?? 0),
      },
    });
  }
  return out;
}

/** Page every column of every relation into `sink` (phase 2). */
export async function fetchMysqlColumnPages(
  connectionId: string,
  database: string,
  sink: MysqlPageSink<MysqlRelationScoped<MysqlSchemaColumn>>,
  options?: PagedFetchOptions,
): Promise<void> {
  await pageMysqlArtefact<MysqlRelationScoped<MysqlSchemaColumn>>({
    connectionId,
    database,
    label: 'cols',
    artefact: 'columns',
    totalBudget: COLUMN_ROW_BUDGET,
    sqlForPage: buildMysqlColumnsPageSql,
    aggregate: aggregateMysqlColumnRows,
    sink,
    shouldAbort: options?.shouldAbort,
  });
}

/** Accumulating wrapper over {@link fetchMysqlColumnPages}. */
async function fetchColumns(connectionId: string, database: string): Promise<Record<string, MysqlSchemaColumn[]>> {
  const byTable: Record<string, MysqlSchemaColumn[]> = {};
  await fetchMysqlColumnPages(connectionId, database, (page) => {
    for (const { table, item } of page) (byTable[table] ??= []).push(item);
  });
  return byTable;
}

// INDEX_TYPE distinguishes BTREE / FULLTEXT / HASH / SPATIAL — needed by the
// dialect-native resolver to restrict MATCH AGAINST column lists to
// FULLTEXT-indexed columns (PRD Slice "MySQL Resolver — Engine Extras").
export function buildMysqlIndexesPageSql(pageClause: string): string {
  return `SELECT TABLE_NAME, INDEX_NAME, NON_UNIQUE, COLUMN_NAME, SEQ_IN_INDEX, INDEX_TYPE
    FROM information_schema.STATISTICS
    WHERE TABLE_SCHEMA = DATABASE()
    ORDER BY TABLE_NAME, INDEX_NAME, SEQ_IN_INDEX
    ${pageClause}`;
}

/**
 * Page the index catalog into `sink`. One index is many rows (one per key
 * column), so the paged loop carries a straddling `(table, index)` group to
 * the next page rather than aggregating it as two partial indexes.
 */
export async function fetchMysqlIndexPages(
  connectionId: string,
  database: string,
  sink: MysqlPageSink<MysqlRelationScoped<MysqlSchemaIndex>>,
  options?: PagedFetchOptions,
): Promise<PagedIntrospectionOutcome> {
  return pageMysqlArtefact<MysqlRelationScoped<MysqlSchemaIndex>>({
    connectionId,
    database,
    label: 'indexes',
    artefact: 'indexes',
    totalBudget: INDEX_ROW_BUDGET,
    sqlForPage: buildMysqlIndexesPageSql,
    aggregate: (rows) => {
      const byTable = aggregateMysqlIndexRows(rows);
      const out: MysqlRelationScoped<MysqlSchemaIndex>[] = [];
      for (const [table, indexes] of Object.entries(byTable)) {
        for (const item of indexes) out.push({ table, item });
      }
      return out;
    },
    sink,
    groupKeyOf: (row) => `${String(row.TABLE_NAME ?? '')}|${String(row.INDEX_NAME ?? '')}`,
    shouldAbort: options?.shouldAbort,
  });
}

/** Accumulating wrapper over {@link fetchMysqlIndexPages}. */
async function fetchIndexes(connectionId: string, database: string): Promise<Record<string, MysqlSchemaIndex[]>> {
  const byTable: Record<string, MysqlSchemaIndex[]> = {};
  await fetchMysqlIndexPages(connectionId, database, (page) => {
    for (const { table, item } of page) (byTable[table] ??= []).push(item);
  });
  return byTable;
}

/**
 * Pure helper — group the per-key-column `STATISTICS` rows into one
 * `MysqlSchemaIndex` per `(table, index)`, keeping `SEQ_IN_INDEX` order (the
 * SQL `ORDER BY` establishes it; this only buckets). Exported for unit tests,
 * mirroring `aggregateMysqlFkRows`.
 *
 * Grouping is a nested map keyed by table THEN index name — not a
 * `"table::index"` string key, which a table or index name containing `::`
 * (legal inside backticks) would split in the wrong place and file under the
 * wrong relation.
 *
 * `PRIMARY` is kept: it is a real index name, and the index-name completion
 * slots (`FORCE INDEX (▮)`, `DROP INDEX ▮`) accept it.
 */
export function aggregateMysqlIndexRows(
  rows: ReadonlyArray<Record<string, unknown>>,
): Record<string, MysqlSchemaIndex[]> {
  const byTable: Record<string, MysqlSchemaIndex[]> = {};
  const seen = new Map<string, Map<string, MysqlSchemaIndex>>();

  for (const row of rows) {
    const tableName = String(row.TABLE_NAME ?? '').trim();
    const indexName = String(row.INDEX_NAME ?? '').trim();
    if (!tableName || !indexName) continue;
    let byName = seen.get(tableName);
    if (!byName) {
      byName = new Map<string, MysqlSchemaIndex>();
      seen.set(tableName, byName);
      byTable[tableName] = [];
    }
    let idx = byName.get(indexName);
    if (!idx) {
      const indexType = row.INDEX_TYPE != null ? String(row.INDEX_TYPE) : undefined;
      idx = {
        name: indexName,
        isUnique: String(row.NON_UNIQUE ?? '1') === '0',
        columns: [],
        ...(indexType ? { indexType } : {}),
      };
      byName.set(indexName, idx);
      byTable[tableName]!.push(idx);
    }
    // A functional key part (`(LOWER(name))`, MySQL 8.0.13+) reports a NULL
    // column; skip it rather than pushing an empty string.
    const columnName = String(row.COLUMN_NAME ?? '').trim();
    if (columnName) idx.columns.push(columnName);
  }
  return byTable;
}

/**
 * MySQL FK introspection — driven by `information_schema.REFERENTIAL_CONSTRAINTS`
 * (FK-specific) joined to `KEY_COLUMN_USAGE` for the per-column rows. Composite
 * FKs come back as one row per `(constraint, ORDINAL_POSITION)` pair; the
 * adapter (`projectForeignKeys` in `mysqlAdapter.ts`) groups by
 * `CONSTRAINT_NAME` to produce a single `FkEdge` with aligned
 * `fromColumns`/`toColumns` arrays.
 *
 * `REFERENTIAL_CONSTRAINTS` is preferred over the more general
 * `TABLE_CONSTRAINTS` because (a) it's FK-only — no `WHERE CONSTRAINT_TYPE`
 * filter needed; (b) it carries `UPDATE_RULE` / `DELETE_RULE` /
 * `MATCH_OPTION` for future use without a second round-trip; and (c) it
 * exposes `UNIQUE_CONSTRAINT_SCHEMA` directly so cross-database FKs can be
 * surfaced once the snapshot grows that capability.
 */
export function buildMysqlForeignKeysPageSql(pageClause: string): string {
  return `SELECT kcu.TABLE_NAME, kcu.CONSTRAINT_NAME, kcu.COLUMN_NAME,
    kcu.REFERENCED_TABLE_NAME, kcu.REFERENCED_COLUMN_NAME
    FROM information_schema.REFERENTIAL_CONSTRAINTS rc
    JOIN information_schema.KEY_COLUMN_USAGE kcu
      ON rc.CONSTRAINT_NAME = kcu.CONSTRAINT_NAME
     AND rc.CONSTRAINT_SCHEMA = kcu.CONSTRAINT_SCHEMA
    WHERE rc.CONSTRAINT_SCHEMA = DATABASE()
    ORDER BY kcu.TABLE_NAME, kcu.CONSTRAINT_NAME, kcu.ORDINAL_POSITION
    ${pageClause}`;
}

/**
 * Page the FK catalog into `sink`. A composite FK is one row per key column,
 * so a straddling `(table, constraint)` group is carried to the next page —
 * which is also what lets the cache append each constraint's projected edge
 * exactly once.
 */
export async function fetchMysqlForeignKeyPages(
  connectionId: string,
  database: string,
  sink: MysqlPageSink<MysqlRelationScoped<MysqlSchemaForeignKey>>,
  options?: PagedFetchOptions,
): Promise<void> {
  await pageMysqlArtefact<MysqlRelationScoped<MysqlSchemaForeignKey>>({
    connectionId,
    database,
    label: 'fks',
    artefact: 'foreignKeys',
    totalBudget: FK_ROW_BUDGET,
    sqlForPage: buildMysqlForeignKeysPageSql,
    aggregate: (rows) => {
      const byTable = aggregateMysqlFkRows(rows);
      const out: MysqlRelationScoped<MysqlSchemaForeignKey>[] = [];
      for (const [table, fks] of Object.entries(byTable)) {
        for (const item of fks) out.push({ table, item });
      }
      return out;
    },
    sink,
    groupKeyOf: (row) => `${String(row.TABLE_NAME ?? '')}|${String(row.CONSTRAINT_NAME ?? '')}`,
    shouldAbort: options?.shouldAbort,
  });
}

/** Accumulating wrapper over {@link fetchMysqlForeignKeyPages}. */
async function fetchForeignKeys(connectionId: string, database: string): Promise<Record<string, MysqlSchemaForeignKey[]>> {
  const byTable: Record<string, MysqlSchemaForeignKey[]> = {};
  await fetchMysqlForeignKeyPages(connectionId, database, (page) => {
    for (const { table, item } of page) (byTable[table] ??= []).push(item);
  });
  return byTable;
}

/**
 * Pure helper — group raw `KEY_COLUMN_USAGE` rows by source table, preserving
 * `ORDINAL_POSITION` order (the SQL `ORDER BY` already does this; the helper
 * just buckets and trims). Exposed for unit tests so the projection contract
 * can be exercised without a live MySQL.
 *
 * Drops rows that are missing any of the required text fields
 * (`TABLE_NAME` / `CONSTRAINT_NAME` / `COLUMN_NAME` /
 * `REFERENCED_TABLE_NAME` / `REFERENCED_COLUMN_NAME`) so a glitchy row
 * cannot corrupt the snapshot.
 */
export function aggregateMysqlFkRows(
  rows: ReadonlyArray<Record<string, unknown>>,
): Record<string, MysqlSchemaForeignKey[]> {
  const byTable: Record<string, MysqlSchemaForeignKey[]> = {};
  for (const row of rows) {
    const tableName = String(row.TABLE_NAME ?? '').trim();
    const constraintName = String(row.CONSTRAINT_NAME ?? '').trim();
    const columnName = String(row.COLUMN_NAME ?? '').trim();
    const referencedTable = String(row.REFERENCED_TABLE_NAME ?? '').trim();
    const referencedColumn = String(row.REFERENCED_COLUMN_NAME ?? '').trim();
    if (!tableName || !constraintName || !columnName || !referencedTable || !referencedColumn) {
      continue;
    }
    const fk: MysqlSchemaForeignKey = {
      constraintName,
      columnName,
      referencedTable,
      referencedColumn,
    };
    if (!byTable[tableName]) byTable[tableName] = [];
    byTable[tableName].push(fk);
  }
  return byTable;
}

export function buildMysqlRoutinesPageSql(pageClause: string): string {
  return `SELECT ROUTINE_NAME, ROUTINE_TYPE
    FROM information_schema.ROUTINES
    WHERE ROUTINE_SCHEMA = DATABASE()
    ORDER BY ROUTINE_NAME
    ${pageClause}`;
}

export function aggregateMysqlRoutineListRows(
  rows: readonly IntrospectionRow[],
): MysqlSchemaRoutine[] {
  return rows.map((row) => ({
    name: String(row.ROUTINE_NAME ?? ''),
    type: (String(row.ROUTINE_TYPE ?? '').toLowerCase() === 'function'
      ? 'function'
      : 'procedure') as 'function' | 'procedure',
  }));
}

/** Page the routine NAME list into `sink` (phase 2). */
export async function fetchMysqlRoutineListPages(
  connectionId: string,
  database: string,
  sink: MysqlPageSink<MysqlSchemaRoutine>,
  options?: PagedFetchOptions,
): Promise<void> {
  await pageMysqlArtefact<MysqlSchemaRoutine>({
    connectionId,
    database,
    label: 'routines',
    artefact: 'routines',
    totalBudget: ROUTINE_ROW_BUDGET,
    sqlForPage: buildMysqlRoutinesPageSql,
    aggregate: aggregateMysqlRoutineListRows,
    sink,
    shouldAbort: options?.shouldAbort,
  });
}

/** Accumulating wrapper over {@link fetchMysqlRoutineListPages}. */
async function fetchRoutines(connectionId: string, database: string): Promise<MysqlSchemaRoutine[]> {
  const out: MysqlSchemaRoutine[] = [];
  await fetchMysqlRoutineListPages(connectionId, database, (page) => {
    out.push(...page);
  });
  return out;
}

/**
 * Probe MySQL session variables that drive identifier handling in the
 * dialect-native resolver:
 *   - `@@sql_mode` — the resolver checks for `ANSI_QUOTES` to decide whether
 *     `"foo"` is an identifier delimiter (vs. string literal).
 *   - `@@lower_case_table_names` — `0` keeps stored case (Linux); `1`/`2`
 *     case-insensitive (managed MySQL / macOS / Windows). The resolver folds
 *     identifier comparisons accordingly.
 *   - `VERSION()` — drives version-gated completion catalogs (`8.0+` CTE,
 *     window functions, JSON_TABLE, etc.).
 *
 * All three are folded into the schema cache so the resolver doesn't repeat
 * the probe on every keystroke. The probe runs once per snapshot refresh
 * (TTL 30s by default) and is cheap (`SELECT @@sql_mode, @@lower_case_table_names, VERSION()`).
 * On probe failure we return safe defaults: empty sqlMode, null
 * lowerCaseTableNames, empty serverVersion — the resolver's identifier
 * handling defaults to backtick-only quoting, which works in every mode.
 */
export async function fetchMysqlSessionVars(
  connectionId: string,
  database: string,
): Promise<{ sqlMode: string; lowerCaseTableNames: number | null; serverVersion: string }> {
  const sql = `SELECT @@sql_mode AS sql_mode, @@lower_case_table_names AS lcase, VERSION() AS version;`;
  const rows = await probeRows(connectionId, database, 'sessionvars', sql);
  const row = rows[0];
  if (!row) {
    return { sqlMode: '', lowerCaseTableNames: null, serverVersion: '' };
  }
  const lcaseRaw = row.lcase;
  const lcase =
    lcaseRaw === null || lcaseRaw === undefined ? null : Number(lcaseRaw);
  return {
    sqlMode: String(row.sql_mode ?? ''),
    lowerCaseTableNames: lcase !== null && Number.isFinite(lcase) ? lcase : null,
    serverVersion: String(row.version ?? ''),
  };
}

function buildLookupIndexes(objects: MysqlSchemaObject[]): Pick<
  MysqlSchemaSnapshot,
  'objectsByNameLower' | 'columnsByObjectNameLower' | 'columnIndexByNameLower'
> {
  const objectsByNameLower: MysqlSchemaSnapshot['objectsByNameLower'] = {};
  const columnsByObjectNameLower: MysqlSchemaSnapshot['columnsByObjectNameLower'] = {};
  const columnIndexByNameLower: MysqlSchemaSnapshot['columnIndexByNameLower'] = {};

  for (const obj of objects) {
    const key = obj.name.toLowerCase();
    objectsByNameLower[key] = obj;
    columnsByObjectNameLower[key] = obj.columns;

    for (const column of obj.columns) {
      const columnKey = column.name.toLowerCase();
      const list = columnIndexByNameLower[columnKey] ?? [];
      list.push({ objectName: obj.name, objectType: obj.type, column });
      columnIndexByNameLower[columnKey] = list;
    }
  }

  return { objectsByNameLower, columnsByObjectNameLower, columnIndexByNameLower };
}

/**
 * Build a COMPLETE snapshot in one call — phase 1 and phase 2 accumulated
 * without publishing. `mysqlSchemaCache` no longer uses this (it drives the
 * two phases itself so the relation list can publish first); it stays as the
 * one-shot form for callers that just want the finished object, and for the
 * unit tests that exercise the query set end to end.
 */
export async function buildMysqlSchemaSnapshot(connectionId: string, database: string): Promise<MysqlSchemaSnapshot> {
  const [objectList, columnsByTable, indexesByTable, fksByTable, routines, databases, routinesByNameLower, sessionVars] = await Promise.all([
    fetchTablesAndViews(connectionId, database),
    fetchColumns(connectionId, database),
    fetchIndexes(connectionId, database),
    fetchForeignKeys(connectionId, database),
    fetchRoutines(connectionId, database),
    fetchMysqlDatabases(connectionId, database),
    fetchMysqlRoutineParams(connectionId, database),
    fetchMysqlSessionVars(connectionId, database),
  ]);

  const objects: MysqlSchemaObject[] = objectList.map((obj) => ({
    ...obj,
    columns: columnsByTable[obj.name] ?? [],
    indexes: indexesByTable[obj.name] ?? [],
    foreignKeys: fksByTable[obj.name] ?? [],
  }));

  const tables = objects.filter((obj) => obj.type === 'table');
  const views = objects.filter((obj) => obj.type === 'view');
  const lookups = buildLookupIndexes(objects);

  return {
    connectionId,
    database,
    builtAt: Date.now(),
    tables,
    views,
    routines,
    databases,
    ...lookups,
    routinesByNameLower,
    sqlMode: sessionVars.sqlMode,
    lowerCaseTableNames: sessionVars.lowerCaseTableNames,
    serverVersion: sessionVars.serverVersion,
    phase: 'complete',
  };
}
